import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';

export const JOURNEY_PUBLICATION = 'JOURNEY_PUBLICATION';
export const JOURNEY_PUBLICATION_PREVIEW = '[行程]';
export const JOURNEY_PUBLICATION_UNAVAILABLE_COPY = '這個行程目前無法查看';
export const JOURNEY_PUBLICATION_RESOLVE_MAX_IDS = 50;
export const JOURNEY_PUBLICATION_CARD_VERSION = 1;

export const RESOLVE_AVAILABLE = 'AVAILABLE';
export const RESOLVE_UNAVAILABLE = 'UNAVAILABLE';
export const RESOLVE_RESTRICTED = 'RESTRICTED';
export const RESOLVE_SOURCE_REMOVED = 'SOURCE_REMOVED';
export const RESOLVE_FAILED = 'RESOLVE_FAILED';

const KNOWN_STATUSES = new Set([
  RESOLVE_AVAILABLE,
  RESOLVE_UNAVAILABLE,
  RESOLVE_RESTRICTED,
  RESOLVE_SOURCE_REMOVED,
  RESOLVE_FAILED,
]);
const PUBLICATION_ID_PATTERN = /^[1-9]\d{0,19}$/;

export function isJourneyPublicationMessage(message) {
  return String(message?.messageType || '') === JOURNEY_PUBLICATION;
}

function isRevoked(message) {
  return String(message?.status || '').toUpperCase() === 'REVOKED' || Boolean(message?.revokedAt);
}

/**
 * 只取 opaque journeyPublicationId（businessId）供 resolve 使用；歷史快照不作為可顯示卡片。
 */
export function journeyPublicationIdOf(message) {
  if (!isJourneyPublicationMessage(message)) return '';
  const businessType = String(message?.businessType || '');
  if (businessType && businessType !== JOURNEY_PUBLICATION) return '';
  const raw = message?.businessId ?? message?.journeyPublicationId;
  const id = raw == null ? '' : String(raw).trim();
  return PUBLICATION_ID_PATTERN.test(id) ? id : '';
}

/** 依訊息順序收集需 resolve 的不重複 publicationId。 */
export function collectJourneyPublicationIds(messages) {
  const ids = [];
  const seen = new Set();
  (messages || []).forEach((message) => {
    if (!isJourneyPublicationMessage(message) || isRevoked(message)) return;
    const id = journeyPublicationIdOf(message);
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });
  return ids;
}

export function chunkJourneyPublicationIds(ids, size = JOURNEY_PUBLICATION_RESOLVE_MAX_IDS) {
  const unique = [...new Set((ids || []).map(String))];
  const chunks = [];
  for (let index = 0; index < unique.length; index += size) {
    chunks.push(unique.slice(index, index + size));
  }
  return chunks;
}

function normalizeAvailableCard(card) {
  if (!card || typeof card !== 'object') return null;
  if (Number(card.snapshotVersion) !== JOURNEY_PUBLICATION_CARD_VERSION) return null;
  const title = typeof card.title === 'string' ? card.title.trim() : '';
  if (!title) return null;
  const dayCount = Number(card.dayCount);
  return {
    title,
    coverFileReference: typeof card.coverFileReference === 'string' ? card.coverFileReference : '',
    destinationSummary: typeof card.destinationSummary === 'string' ? card.destinationSummary.trim() : '',
    dayCount: Number.isInteger(dayCount) && dayCount > 0 && dayCount <= 366 ? dayCount : null,
  };
}

/**
 * 將 resolve 回應對應回本次請求的 ID；未知狀態、缺漏或畸形卡片一律 fail closed。
 */
export function indexJourneyPublicationResolve(requestedIds, response) {
  const requested = new Set((requestedIds || []).map(String));
  const byId = {};
  const items = Array.isArray(response?.items) ? response.items : [];
  items.forEach((item) => {
    const id = item?.journeyPublicationId == null ? '' : String(item.journeyPublicationId);
    if (!requested.has(id) || byId[id]) return;
    const status = String(item?.status || '');
    if (status === RESOLVE_AVAILABLE) {
      const card = normalizeAvailableCard(item?.card);
      byId[id] = card ? { status: RESOLVE_AVAILABLE, card } : { status: RESOLVE_UNAVAILABLE, card: null };
      return;
    }
    byId[id] = { status: KNOWN_STATUSES.has(status) ? status : RESOLVE_UNAVAILABLE, card: null };
  });
  requested.forEach((id) => {
    if (!byId[id]) byId[id] = { status: RESOLVE_FAILED, card: null };
  });
  return byId;
}

export function failedJourneyPublicationResolve(ids) {
  const byId = {};
  (ids || []).forEach((id) => { byId[String(id)] = { status: RESOLVE_FAILED, card: null }; });
  return byId;
}

/** 卡片呈現只依 resolve 結果；非 AVAILABLE 不帶任何行程細節。 */
export function journeyPublicationCardView(publicationId, resolution) {
  if (!PUBLICATION_ID_PATTERN.test(String(publicationId || ''))) return { state: 'unavailable' };
  if (!resolution) return { state: 'loading' };
  if (resolution.status === RESOLVE_AVAILABLE && resolution.card) {
    const card = normalizeAvailableCard({ ...resolution.card, snapshotVersion: JOURNEY_PUBLICATION_CARD_VERSION });
    if (!card) return { state: 'unavailable' };
    return {
      state: 'available',
      href: `/public/journeys/${publicationId}`,
      title: card.title,
      coverUrl: card.coverFileReference ? resolveMemberMediaUrl(card.coverFileReference) : '',
      destinationSummary: card.destinationSummary,
      dayCount: card.dayCount,
    };
  }
  if (resolution.status === RESOLVE_FAILED) return { state: 'failed' };
  return { state: 'unavailable' };
}
